import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPlans, subscribeToPlan } from "../../../../../Redux/Subscription/subscriptionSlice";
import { fetchUserProfile } from "../../../../../Redux/Auth/authSlice";
import { getIdToken } from "firebase/auth";
import { auth } from "../../../../../firebase";
import axios from "axios";
import { API_ROUTES } from "../../../../../Redux/Constants/apiRoutes";
import '../../../../../Stylesheets/Profile/BillingTab.css';
import { showFeedback } from "../../../../../Redux/UI/uiFeedbackSlice";

/**
 * Modern, stylish redesign of the Billing tab (React + CSS only)
 * - No UI libs, no Tailwind
 * - Keeps your Redux + Firebase logic
 * - Polished Pro modal + loading overlay
 * - Invoices table with graceful empty state
 */
export default function BillingTabModern() {
  const dispatch = useDispatch();

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const pollRef = useRef(null);


  const { allPlans = [], currentPlan } = useSelector((s) => s.subscription || {});
  const { user } = useSelector((s) => s.auth || {});

  const proPlan = useMemo(
    () => (Array.isArray(allPlans) ? allPlans.find((p) => p.name?.toLowerCase() === "pro") : null),
    [allPlans]
  );

  const proFeatures = useMemo(() => {
    const f = proPlan?.features;

    // If backend already sends an array, just use it.
    if (Array.isArray(f)) return f;

    // If it's an object of booleans, convert to human-readable bullets.
    if (f && typeof f === 'object') {
      const labels = {
        bulkAttendance: 'Bulk attendance',
        exports: 'CSV/PDF exports',
        analytics: 'Advanced analytics',
        aiSuggestions: 'AI suggestions',
      };
      return Object.entries(f)
        .filter(([, enabled]) => !!enabled)
        .map(([key]) => labels[key] || key);
    }

    // Fallback marketing copy
    return ['Unlimited projects', 'Advanced analytics', 'Priority support'];
  }, [proPlan]);


  useEffect(() => {
  console.log("allPlans:", allPlans);
  console.log("proPlan:", proPlan);
}, [allPlans, proPlan]);



  useEffect(() => {
    dispatch(fetchPlans());
    fetchProfile();
    loadInvoices();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showModal) return;

    const onKey = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);


  const fetchProfile = async () => {
    const token = await auth.currentUser?.getIdToken();
    if (token) dispatch(fetchUserProfile(token));
  };

  const loadInvoices = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      const res = await axios.get(API_ROUTES.GET_MY_INVOICES, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to load invoices:", err);
      setInvoices([]);
      dispatch(
        showFeedback({
          type: "error",
          title: "Invoices failed",
          message: "Could not load invoices. Please try again.",
        })
      );
    }

  };

  // Poll after Razorpay checkout to reflect Pro status + invoices
  const startPollingForSubscription = () => {
    let attempts = 0;
    const maxAttempts = 12;

    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) {
          const payload = await dispatch(fetchUserProfile(token)).unwrap().catch(() => null);
          const plan = (payload?.plan || payload?.user?.plan || "").toLowerCase();
          const valid = payload?.planValidTill || payload?.user?.planValidTill;

          if (plan === "pro" && valid) {
            clearInterval(pollRef.current);
            pollRef.current = null;

            await loadInvoices();
            setIsSubscribing(false);
            setShowFallback(false);

            dispatch(
              showFeedback({
                type: "success",
                title: "Pro activated",
                message: "Your plan is now active.",
              })
            );
            return;
          }
        }

        attempts += 1;
      } catch (e) {
        attempts += 1;
      }
      if (attempts >= maxAttempts) {
        clearInterval(pollRef.current);
        pollRef.current = null;

        setShowFallback(true);
        setIsSubscribing(false);

        dispatch(
          showFeedback({
            type: "warning",
            title: "Verification taking longer",
            message: "If your payment succeeded, refresh after a minute.",
          })
        );
      }
    }, 3000);
  };


  const handleUpgradeClick = async () => {
    if (!proPlan?._id) {
      dispatch(
        showFeedback({
          type: "error",
          title: "Plan missing",
          message: "Pro plan is not available right now. Please refresh.",
        })
      );
      return;
    }

    if (!auth.currentUser) {
      dispatch(
        showFeedback({
          type: "error",
          title: "Not signed in",
          message: "Please sign in again to upgrade your plan.",
        })
      );
      return;
    }

    setIsSubscribing(true);
    setShowFallback(false);
    setShowModal(false);

    try {
      const token = await getIdToken(auth.currentUser, true);

      const res = await dispatch(
        subscribeToPlan({
          planId: proPlan._id,
          userToken: token,
        })
      ).unwrap();

      // ✅ Only poll when payment actually happened
      if (res?.status === "paid") {
        startPollingForSubscription();
      } else {
        // cancelled
        setIsSubscribing(false);
      }
    } catch (e) {
      // thunk already shows feedback
      setIsSubscribing(false);
    }
  };


  const planName = (p) => {
    const s = typeof p === "string" ? p : "free";
    return s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : "Free";
  };

  const isPro = (currentPlan || "").toLowerCase() === "pro";

  return (
    <div className="btm-wrap">
      {/* Header */}
      <div className="btm-header">
        <div>
          <h1 className="btm-title">
            Billing {isPro ? <span className="btm-badge btm-badge-gold">Pro</span> : <span className="btm-badge">Free</span>}
          </h1>
          <p className="btm-sub">Manage your plan, invoices, and subscription.</p>
        </div>
        {!isPro && (
          <button className="btm-btn btm-btn-primary" onClick={() => setShowModal(true)}>Upgrade to Pro</button>
        )}
      </div>

      {/* Top row */}
      <div className="btm-grid">
        {/* Current Plan card */}
        <div className="btm-card btm-card-hero">
          <div className="btm-card-head">
            <span className="btm-label">Current Plan</span>
            {isPro ? <span className="btm-chip btm-chip-active">Active</span> : <span className="btm-chip">Free</span>}
          </div>
          <div className="btm-plan-name">{planName(currentPlan) || "Free"}</div>

          <div className="btm-kv-grid">
            <div className="btm-kv">
              <div className="btm-kv-label">Billing Email</div>
              <div className="btm-kv-value">{user?.email || "–"}</div>
            </div>
            <div className="btm-kv">
              <div className="btm-kv-label">Next Billing Date</div>
              <div className="btm-kv-value">
                {user?.paymentDetails?.nextBillingDate
                  ? new Date(user.paymentDetails.nextBillingDate).toLocaleDateString()
                  : "–"}
              </div>
            </div>
          </div>

          {!isPro && (
            <div className="btm-cta">
              <button className="btm-btn btm-btn-ghost" onClick={() => setShowModal(true)}>Upgrade to Pro</button>
              <span className="btm-cta-note">Unlock advanced features and priority support.</span>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="btm-stats">
          <div className="btm-stat">
            <div className="btm-stat-label">Invoices</div>
            <div className="btm-stat-val">{invoices?.length || 0}</div>
          </div>
          <div className="btm-stat">
            <div className="btm-stat-label">Status</div>
            <div className="btm-stat-val">{isPro ? "Active" : "Free tier"}</div>
          </div>
        </div>
      </div>
      <br></br>
      {/* Invoices table */}
      <div className="btm-card">
        <div className="btm-card-title">Invoices</div>
        {invoices?.length ? (
          <div className="btm-table-wrap">
            <table className="btm-table">
              <thead>
                <tr>
                  <th className="w40">Issued On</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="tr">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const issued = inv?.issuedAt ? new Date(inv.issuedAt).toLocaleDateString() : "–";
                  const amount = inv?.amount != null ? `₹${Number(inv.amount).toLocaleString()}` : "–";
                  const status = inv?.status ? inv.status.replace(/_/g, " ") : "paid";
                  const canOpen = Boolean(inv?.pdfUrl);
                  return (
                    <tr key={inv._id}>
                      <td className="fw">{issued}</td>
                      <td>{amount}</td>
                      <td>
                        <span className={`btm-badge ${status === "paid" ? "btm-badge-ok" : ""}`}>{status}</span>
                      </td>
                      <td className="tr">
                        {canOpen ? (
                          <a className="btm-link" href={inv.pdfUrl} target="_blank" rel="noopener noreferrer">Download</a>
                        ) : (
                          <button
                            className="btm-btn btm-btn-outline"
                            onClick={() =>
                              dispatch(
                                showFeedback({
                                  type: "info",
                                  title: "Invoice not ready",
                                  message: "Invoice PDF is not available yet. Please check again later.",
                                })
                              )
                            }
                          >
                            Pending
                          </button>

                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="btm-empty">
            <div className="btm-empty-icon" aria-hidden>⬇️</div>
            <div className="btm-empty-text">No invoices found yet.</div>
          </div>
        )}
      </div>

      {/* Upgrade Modal – Beautiful/Modern */}
      {showModal && (
        <div
          className="btm-modal-overlay btm-fade-in"
          role="dialog"
          aria-modal="true"
          aria-labelledby="btm-upgrade-title"
          onMouseDown={() => setShowModal(false)}
        >
          <div className="btm-modal btm-scale-in" role="document" onMouseDown={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="btm-modal-head">
              <div className="btm-modal-titlewrap">
                <div className="btm-modal-icon" aria-hidden="true">
                  {/* Starburst icon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l2.1 4.8L19 9l-4.3 2.1L13 16l-1-4.9L7 9l4.9-.2L12 2z" />
                  </svg>
                </div>
                <div>
                  <div id="btm-upgrade-title" className="btm-modal-title">Upgrade to Pro</div>
                  <div className="btm-modal-sub">Unlock advanced controls, higher limits, and priority support.</div>
                </div>
              </div>

              <button
                className="btm-modal-close"
                aria-label="Close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            {/* Plan Card */}
            <div className="btm-plan-card">
              <div className="btm-plan-ribbon">Most Popular</div>

              <div className="btm-plan-head">
                <div className="btm-plan-head-l">
                  <div className="btm-plan-name">Pro</div>
                  <div className="btm-plan-desc">Everything in Free, plus:</div>
                </div>
                <div className="btm-price">
                  <span className="btm-currency">₹</span>
                  <span className="btm-amt">{proPlan?.price ?? '—'}</span>
                  <span className="btm-unit">/mo</span>
                </div>
              </div>

              <div className="btm-divider"></div>

              <ul className="btm-feature-grid" role="list">
                {(Array.isArray(proFeatures) ? proFeatures : [
                  'Unlimited projects',
                  'Advanced analytics',
                  'Priority support',
                  'Team roles & permissions',
                  'Export & reports (CSV/PDF)',
                  'Usage alerts & webhooks'
                ]).map((f, i) => (
                  <li key={i} className="btm-feature-item">
                    <span className="btm-check" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="btm-feature-text">{f}</span>
                  </li>
                ))}
              </ul>

              {/* Trust row */}
              <div className="btm-trust-row">
                <span className="btm-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M12 2l7 4v6c0 5-3.8 9.4-7 10-3.2-.6-7-5-7-10V6l7-4z" />
                  </svg>
                  Secure checkout
                </span>
                <span className="btm-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M13 2v9h9v2h-9v9h-2v-9H2v-2h9V2z" />
                  </svg>
                  Cancel anytime
                </span>
                <span className="btm-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4c1.74 0 3.41.81 4.5 2.09C12.09 4.81 13.76 4 15.5 4 18 4 20 6 20 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                  </svg>
                  Loved by teams
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="btm-modal-actions">
              <button className="btm-btn btm-btn-ghost" onClick={() => setShowModal(false)}>
                Maybe later
              </button>
              <button className="btm-btn btm-btn-primary" onClick={handleUpgradeClick}disabled={isSubscribing}>
                {isSubscribing ? "Starting…" : "Buy Pro"}
              </button>
            </div>

            {/* Fine print */}
            <p className="btm-guarantee">No long-term lock-in. Taxes may apply.</p>
          </div>
        </div>
      )}


      {/* Loading / Verifying overlay */}
      {isSubscribing && (
        <div className="btm-loading" role="alert" aria-live="polite">
          <div className="btm-loading-box">
            <div className="btm-spinner" />
            <p className="btm-loading-text">
              {showFallback ? "Still verifying… please refresh if nothing changes." : "Verifying your payment…"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
